import { create } from 'zustand'
import apiHelper from '@/network/api'

interface Channel {
  id: string
  teamId: string
  name: string
  type: string
  configuration: {
    webhookUrl: string
  }
  isActive: boolean
  createdAt: string
  updatedAt: string
  SubscriptionChannel?: {
    channelSpecificConfig: Record<string, any>
    isActive: boolean
  }
}

interface NotificationSubscription {
  id: string
  teamId: string
  featureCode: string
  configuration: Record<string, any>
  isActive: boolean
  createdAt: string
  updatedAt: string
  channels: Channel[]
}

interface CreateSubscriptionPayload {
  configuration: Record<string, any>
  channels: Array<{ channelId: string }>
  featureCode: string
}

interface UpdateSubscriptionPayload {
  channels: Array<{ channelId: string }>
  configuration: Record<string, any>
  isActive?: boolean
}

interface NotificationSubscriptionState {
  subscriptions: NotificationSubscription[]
  isLoading: boolean
  error: string | null
  fetchSubscriptions: () => Promise<void>
  createSubscription: (payload: CreateSubscriptionPayload) => Promise<{
    success: boolean
    error?: string
  }>
  updateSubscription: (id: string, payload: UpdateSubscriptionPayload) => Promise<{
    success: boolean
    error?: string
  }>
  deleteSubscription: (id: string) => Promise<{
    success: boolean
    error?: string
  }>
  findSubscription: (featureCode: string, id: string) => Promise<{
    success: boolean
    data?: NotificationSubscription
    error?: string
  }>
}

const useNotificationSubscriptionStore = create<NotificationSubscriptionState>((set, get) => ({
  subscriptions: [],
  isLoading: false,
  error: null,

  fetchSubscriptions: async () => {
    try {
      set({ isLoading: true, error: null })
      const response = await apiHelper(null).get('notification_subscription/list')
      
      if (response.status === 200) {
        set({ 
          subscriptions: response.data,
          isLoading: false 
        })
      }
    } catch (error) {
      set({ 
        error: 'Failed to fetch notification subscriptions',
        isLoading: false
      })
      console.error('Error fetching notification subscriptions:', error)
    }
  },

  createSubscription: async (payload: CreateSubscriptionPayload) => {
    try {
      const response = await apiHelper(null).post('notification_subscription', payload)
      if (response.status === 200 || response.status === 201) {
        await get().fetchSubscriptions()
        return { success: true }
      }
      return { success: false }
    } catch (error: any) {
      if (error?.response?.data) {
        return { success: false, error: error.response.data }
      }
      throw error
    }
  },

  updateSubscription: async (id: string, payload: UpdateSubscriptionPayload) => {
    try {
      const response = await apiHelper(null).put(`notification_subscription/${id}`, payload)
      if (response.status === 200) {
        await get().fetchSubscriptions()
        return { success: true }
      }
      return { success: false }
    } catch (error: any) {
      if (error?.response?.data) {
        return { success: false, error: error.response.data }
      }
      throw error
    }
  },

  deleteSubscription: async (id: string) => {
    try {
      const response = await apiHelper(null).delete(`notification_subscription/${id}`)
      if (response.status === 200 || response.status === 204) {
        await get().fetchSubscriptions()
        return { success: true }
      }
      return { success: false }
    } catch (error: any) {
      if (error?.response?.data) {
        return { success: false, error: error.response.data }
      }
      throw error
    }
  },

  findSubscription: async (featureCode: string, id: string) => {
    try {
      const response = await apiHelper(null).get(`notification_subscription/find?featureCode=${featureCode}&id=${id}`)
      if (response.status === 200) {
        return {
          success: true,
          data: response.data
        }
      }
      return { success: false }
    } catch (error: any) {
      if (error?.response?.status === 404) {
        return { success: false }
      }
      if (error?.response?.data) {
        return { success: false, error: error.response.data }
      }
      throw error
    }
  }
}))

export default useNotificationSubscriptionStore
